import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { WS_URL } from '../constants';
import api from '../services/api';

/**
 * WebSocket hook for real-time market updates
 */
export const useWebSocket = (onMessage) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const connectRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);

  // Store the latest onMessage callback
  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    const connect = () => {
      try {
        wsRef.current = new WebSocket(WS_URL);
        
        wsRef.current.onopen = () => {
          setIsConnected(true);
          // Show reconnect success if this was a reconnection
          if (wsRef.current._reconnectAttempt > 0) {
            toast.success('Verbindung wiederhergestellt', { duration: 2000 });
          }
          // Send ping every 30 seconds to keep alive
          const pingInterval = setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ action: 'ping' }));
            }
          }, 30000);
          wsRef.current.pingInterval = pingInterval;
        };
        
        wsRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            setLastMessage(data);
            if (onMessageRef.current) onMessageRef.current(data);
          } catch (e) {
            console.error('WebSocket message parse error:', e);
          }
        };
        
        wsRef.current.onclose = (event) => {
          setIsConnected(false);
          if (wsRef.current?.pingInterval) {
            clearInterval(wsRef.current.pingInterval);
          }
          // Reconnect with exponential backoff + jitter
          const attempt = wsRef.current?._reconnectAttempt || 0;
          if (attempt >= 10) {
            console.warn(
              `WebSocket: max reconnect attempts reached (last close code=${event?.code} reason=${event?.reason || 'n/a'})`
            );
            toast.error('Verbindung zum Server verloren. Bitte Seite neu laden.');
            return;
          }
          const baseDelay = Math.min(1000 * Math.pow(2, attempt), 30000);
          const jitter = Math.random() * 1000;
          const delay = baseDelay + jitter;
          reconnectTimeoutRef.current = setTimeout(() => {
            if (connectRef.current) {
              const ws = connectRef.current();
              if (ws) ws._reconnectAttempt = attempt + 1;
            }
          }, delay);
        };

        // The browser fires `onerror` with a generic Event whenever the
        // socket transitions into CLOSED — including expected
        // disconnects during reconnect. The Event carries no useful
        // detail (just `[object Event]`); the close event right after
        // it carries the actual close code + reason. So we keep this
        // handler silent and let onclose log if needed.
        wsRef.current.onerror = () => {};
      } catch (error) {
        console.error('WebSocket connection failed:', error);
      }
    };

    connectRef.current = connect;
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current?.pingInterval) {
        clearInterval(wsRef.current.pingInterval);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const subscribe = useCallback((marketId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'subscribe', market_id: marketId }));
    }
  }, []);

  const unsubscribe = useCallback((marketId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'unsubscribe', market_id: marketId }));
    }
  }, []);

  const authenticateUser = useCallback(async (userId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN && userId) {
      try {
        // ueber die api-Instanz, damit der Authorization: Bearer-Header
        // automatisch mitgeht (frueher roher fetch mit credentials:
        // 'include' -> haengte am Cookie, das es jetzt nicht mehr gibt).
        const res = await api.auth.wsToken();
        if (res.data?.ws_token) {
          wsRef.current.send(JSON.stringify({ action: 'auth', token: res.data.ws_token }));
        }
      } catch (err) {
        console.error('WS auth token fetch failed:', err);
      }
    }
  }, []);

  return { isConnected, lastMessage, subscribe, unsubscribe, authenticateUser };
};

/**
 * Pull to refresh hook for mobile
 */
export const usePullToRefresh = (onRefresh, options = {}) => {
  const { threshold = 80, resistance = 2.5 } = options;
  const containerRef = useRef(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const isPulling = useRef(false);

  const handleTouchStart = useCallback((e) => {
    if (containerRef.current?.scrollTop === 0) {
      startY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling.current || isRefreshing) return;
    
    const currentY = e.touches[0].clientY;
    const diff = (currentY - startY.current) / resistance;
    
    if (diff > 0) {
      setPullDistance(Math.min(diff, threshold * 1.5));
    }
  }, [isRefreshing, resistance, threshold]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling.current) return;
    isPulling.current = false;
    
    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    }
    setPullDistance(0);
  }, [pullDistance, threshold, isRefreshing, onRefresh]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, threshold, isRefreshing, onRefresh]);

  return {
    containerRef,
    pullDistance,
    isRefreshing,
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    }
  };
};

/**
 * Online status hook
 */
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

/**
 * Local storage hook
 */
export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(error);
      return initialValue;
    }
  });

  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(error);
    }
  };

  return [storedValue, setValue];
};
